import { FC } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { useCatalogEntrySearch } from '@/components/blocks/DpCatalogEntrySearch/useCatalogEntrySearch';
import { SponsoredProductCard } from '@/components/blocks/DpCatalogEntrySearch/parts/SponsoredProductCard';
import { SponsoredHeroMedia } from '@/components/blocks/DpCatalogEntrySearch/parts/SponsoredHeroMedia';
import {
	sponsoredRootSX,
	sponsoredHeaderSX,
	sponsoredLogoSX,
	sponsoredRowSX,
	sponsoredHeroSX,
	sponsoredProductsSX,
	sponsoredSkeletonSX,
} from '@/components/blocks/DpCatalogEntrySearch/styles/container';
import { sponsoredTitleSX, sponsoredLinkSX } from '@/components/blocks/DpCatalogEntrySearch/styles/typography';

type Props = {
	id?: string;
	logosrc?: string;
	logoalt?: string;
	title: string;
	linkhref?: string;
	linklabel?: string;
	image?: string;
	video?: string;
	skus: string | string[];
};

export const DpCatalogEntrySearch: FC<Props> = ({
	id,
	logosrc,
	logoalt = '',
	title,
	linkhref,
	linklabel,
	image,
	video,
	skus,
}) => {
	const { skuList, products, loading } = useCatalogEntrySearch(skus);

	if (!loading && products.length === 0) {
		return null;
	}

	return (
		<Box component="section" id={id} sx={sponsoredRootSX} aria-label={`Contenido patrocinado por ${title}`}>
			<Box component="header" sx={sponsoredHeaderSX}>
				{logosrc ? (
					<Box component="span" sx={sponsoredLogoSX}>
						<img src={logosrc} alt={logoalt} />
					</Box>
				) : null}
				<Typography component="h2" sx={sponsoredTitleSX}>
					{title}
				</Typography>
				{linkhref && linklabel ? (
					<Linkable href={linkhref} sx={sponsoredLinkSX}>
						{linklabel}
					</Linkable>
				) : null}
			</Box>

			<Box sx={sponsoredRowSX}>
				{video || image ? (
					<Box sx={sponsoredHeroSX}>
						<SponsoredHeroMedia video={video} image={image} title={title} />
					</Box>
				) : null}

				<Box sx={sponsoredProductsSX}>
					{loading
						? skuList.map((sku) => (
								<Skeleton
									key={`dp_catalog_entry_search_skeleton_${sku}`}
									variant="rectangular"
									height={260}
									sx={sponsoredSkeletonSX}
								/>
						  ))
						: products.map((product) => (
								<SponsoredProductCard
									key={`dp_catalog_entry_search_${product.partNumber}`}
									product={product}
								/>
						  ))}
				</Box>
			</Box>
		</Box>
	);
};
